import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { SCHOOL_INSTALLMENT_INVOICE_BY_INSTALLMENT_ID } from "../../../apiRoutes";
import axiosAuth from "../../../../axiosAuth";
import { FILE_UPLOAD } from "../../../apiRoutes";
import { toYMD } from "@/lib/utils";

export interface UpdateInvoicePayload {
  installmentId: string;
  invoiceCode?: string;
  invoiceOwner?: string;
  bankAccountDetail?: string;
  invoiceSubmissionDate?: string;
  invoiceRemark?: string;
  uploadedInvoice?: File | string | null;
}

const uploadInvoiceAndGetUuid = async (file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("file_type", "third_party_invoice");
  const res = await axiosAuth.post(FILE_UPLOAD, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res?.data?.data?.attachment_id as string;
};

export const useUpdateInvoiceMutation = (options?: {
  onSuccess?: () => void;
  onError?: () => void;
}) => {
  return useMutation({
    mutationFn: async (payload: any) => {
      let invoiceFileUuid: string | undefined;
      if (payload.invoiceFile instanceof File) {
        invoiceFileUuid = await uploadInvoiceAndGetUuid(payload.invoiceFile);
      }

      const { installmentId, invoiceFile, ...rest } = payload as any;

      const body: Partial<UpdateInvoicePayload> = {
        invoiceCode: payload.invoiceCode,
        invoiceOwner: payload.invoiceOwner,
        bankAccountDetail: payload.bankAccountDetail,
        invoiceRemark: payload.invoiceRemark,
        invoiceSubmissionDate: payload.invoiceSubmissionDate
          ? toYMD(payload.invoiceSubmissionDate)
          : undefined,
        uploadedInvoice:
          invoiceFileUuid ??
          (typeof payload.invoiceFile === "string"
            ? payload.invoiceFile
            : undefined),
      };

      // Remove undefined values
      const cleanBody = Object.fromEntries(
        Object.entries(body).filter(([, v]) => v !== undefined && v !== null)
      );

      const response = await axiosAuth.put(
        `${SCHOOL_INSTALLMENT_INVOICE_BY_INSTALLMENT_ID}${payload.installmentId}/`,
        cleanBody
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success("Invoice info updated successfully");
      options?.onSuccess?.();
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.issues?.message ||
        error?.response?.data?.message ||
        "Failed to update commercial info";
      toast.error(message);
    },
  });
};
